type GTagEvent = {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export const GA_MEASUREMENT_ID = "G-BFT129MKQV";

/**
 * Sends a Google Analytics event via the client-side gtag.js library.
 * Supports a callback or redirect URL for tracking events before navigation.
 */
export const sendGAEvent = (
  { action, category, label, value, ...rest }: GTagEvent,
  options?: {
    onComplete?: () => void;
    redirectUrl?: string;
    timeout?: number;
  }
) => {
  if (typeof window === "undefined" || !window.gtag) {
    console.warn("Google Analytics not initialized");
    if (options?.redirectUrl) {
      window.location.href = options.redirectUrl;
    } else if (options?.onComplete) {
      options.onComplete();
    }
    return;
  }

  const { onComplete, redirectUrl, timeout = 1000 } = options || {};

  // Helper to handle completion (callback or redirect) safely
  let completed = false;
  const handleComplete = () => {
    if (completed) return;
    completed = true;
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else if (onComplete) {
      onComplete();
    }
  };

  // Set safety timeout
  if (onComplete || redirectUrl) {
    setTimeout(handleComplete, timeout);
  }

  window.gtag("event", action, {
    event_category: category,
    event_label: label,
    value: value,
    event_callback: onComplete || redirectUrl ? handleComplete : undefined,
    event_timeout: onComplete || redirectUrl ? timeout : undefined,
    ...rest,
  });
};

/**
 * Sends a Google Analytics event server-side using the Measurement Protocol.
 * Requires GA_API_SECRET to be set in environment variables.
 */
export const sendServerGAEvent = async (
  userId: string,
  eventName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: Record<string, any> = {}
) => {
  const apiSecret = process.env.GA_API_SECRET;
  
  if (!apiSecret) {
    console.warn("GA_API_SECRET is not set, skipping server-side event:", eventName);
    return;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await fetch(
      `https://www.google-analytics.com/mp/collect?measurement_id=${GA_MEASUREMENT_ID}&api_secret=${apiSecret}`,
      {
        method: "POST",
        body: JSON.stringify({
          client_id: userId, // Using userId as client_id for server-side events if typical client_id cookie isn't available
          user_id: userId,
          events: [
            {
              name: eventName,
              params: {
                ...params,
                engagement_time_msec: 1, // Default to simulate engagement
              },
            },
          ],
        }),
        signal: controller.signal,
      }
    ).finally(() => clearTimeout(timeoutId));

    if (!response.ok) {
      console.error(
        `Failed to send GA event ${eventName}: ${response.status} ${response.statusText}`
      );
    } else {
      // console.log(`Sending GA Event (${eventName}) for user ${userId}`);
    }
  } catch (error) {
    console.error(`Error sending GA event ${eventName}:`, error);
  }
};

// Conversion funnel event helpers
export const trackViewPricing = () => {
  sendGAEvent({
    action: "view_pricing",
    category: "conversion",
    label: "pricing_page_viewed",
  });
};

export const trackGenerationCreated = (params: {
  isFreePreview: boolean;
  resolution: string;
  userId: string;
}) => {
  sendServerGAEvent(params.userId, "generation_created", {
    is_free_preview: params.isFreePreview,
    resolution: params.resolution,
  });
};

export const trackUpgradePromptShown = (params: {
  trigger: "after_generation" | "after_comparison" | "on_download" | "on_try_another";
  isFreePreview: boolean;
}) => {
  sendGAEvent({
    action: "upgrade_prompt_shown",
    category: "conversion",
    label: params.trigger,
    is_free_preview: params.isFreePreview,
  });
};

export const trackFreeExit = (params: {
  exitPoint: "download_attempt" | "comparison_view" | "try_another" | "unlock_attempt";
  generationId?: string;
}) => {
  sendGAEvent({
    action: "free_exit",
    category: "conversion",
    label: params.exitPoint,
    generation_id: params.generationId,
  });
};

// Add type definition for window.gtag
declare global {
  interface Window {
    gtag: (
      command: "config" | "event" | "js",
      targetId: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Google Analytics accepts arbitrary parameters
      config?: Record<string, any>
    ) => void;
  }
}
